import { CircularProgress } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import logoGeoranking from "../../../assets/images/logo-georanking.png";
import { RankingLink } from "../../../components/ranking-link";
import { linkDaConsulta, rankingExibido } from "../../dashboard/pages/keyword-link";
import { KeywordsChart } from "../components/keywords-chart";
import { ReportsService } from "../services";
import { KeywordResponse } from "../types";
// import { KeywordDetailsModal } from "../components/keyword-details-modal";
import {
  ActionButtons,
  ActionsBar,
  Checkbox,
  CheckboxCell,
  Container,
  ContentContainer,
  EmptyState,
  FilterGroup,
  FilterLabel,
  FiltersSection,
  HeaderSection,
  KeywordCell,
  LoadingContainer,
  LogoImg,
  MonthCell,
  Pagination,
  PaginationButton,
  PaginationButtons,
  PaginationInfo,
  SearchInput,
  SecondaryButton,
  SelectionInfo,
  Subtitle,
  Table,
  TableBody,
  TableContainer,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Title,
  TopContainer,
  TotalCell,
  TrendBadge,
  UpdateButton,
} from "./styles";

export const Reports: React.FC = () => {
  const navigate = useNavigate();
  const { profileId } = useParams<{ profileId: string }>();

  const [isLoading, setIsLoading] = useState(true);
  const [keywords, setKeywords] = useState<KeywordResponse[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  // const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  // const [selectedKeywordDetail, setSelectedKeywordDetail] = useState<KeywordResponse | null>(null);
  const itemsPerPage = 10;

  const fetchKeywordsData = async () => {
    if (!profileId) return;

    setIsLoading(true);
    try {
      const data = await ReportsService.getKeywords(profileId);
      setKeywords(data);
      if (data.length === 0) {
        toast.info("Nenhuma palavra-chave encontrada para este perfil");
      } else {
        toast.success(`${data.length} palavras-chave carregadas`);
      }
    } catch (error) {
      console.error("Erro ao buscar dados de palavras-chave:", error);
      toast.error("Erro ao carregar dados. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (profileId) {
      fetchKeywordsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  const handleUpdate = () => {
    fetchKeywordsData();
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedKeywords(filteredKeywords.map((k) => String(k.id)));
    } else {
      setSelectedKeywords([]);
    }
  };

  const handleSelectKeyword = (id: string) => {
    setSelectedKeywords((prev) => {
      if (prev.includes(id)) {
        return prev.filter((k) => k !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // const handleOpenKeywordDetails = (keyword: KeywordResponse) => {
  //   setSelectedKeywordDetail(keyword);
  //   setDetailsModalOpen(true);
  // };

  const getTrendInfo = (diff: number) => {
    if (diff === 0) return { trend: "neutral" as const, display: "-" };
    if (diff < 0) return { trend: "up" as const, display: `${diff}` };
    return { trend: "down" as const, display: `+${diff}` };
  };

  const filteredKeywords = keywords.filter((keyword) =>
    keyword.title.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const paginatedKeywords = filteredKeywords.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const totalPages = Math.ceil(filteredKeywords.length / itemsPerPage);

  return (
    <Container>
      <TopContainer>
        <LogoImg
          src={logoGeoranking}
          alt="GeoRanking"
          onClick={() => navigate("/home")}
        />
      </TopContainer>

      <ContentContainer>
        <HeaderSection>
          <Title>Gerenciar Perfil</Title>
          <Subtitle>
            Análise detalhada de palavras-chave e desempenho no Google
          </Subtitle>
        </HeaderSection>

        <FiltersSection>
          <FilterGroup>
            <FilterLabel>Buscar Palavra-Chave</FilterLabel>
            <SearchInput
              type="text"
              placeholder="Buscar Palavra-Chave"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </FilterGroup>

          <UpdateButton onClick={handleUpdate}>Atualizar</UpdateButton>
        </FiltersSection>

        <ActionsBar>
          <SelectionInfo>
            Selecione <span>entre 2 e 10 palavras-chave para comparar.</span>
          </SelectionInfo>
          <ActionButtons>
            <SecondaryButton
              onClick={() => {
                if (
                  selectedKeywords.length >= 2 &&
                  selectedKeywords.length <= 10
                ) {
                  toast.success("Maximizando visualização...");
                } else {
                  toast.warning("Selecione entre 2 e 10 palavras-chave");
                }
              }}
            >
              Maximizar
            </SecondaryButton>
            <SecondaryButton onClick={() => toast.info("Opções adicionais")}>
              Opções ▼
            </SecondaryButton>
          </ActionButtons>
        </ActionsBar>

        <TableContainer>
          {isLoading ? (
            <LoadingContainer>
              <CircularProgress />
            </LoadingContainer>
          ) : paginatedKeywords.length === 0 ? (
            <EmptyState>
              <div className="icon">📊</div>
              <h3>Nenhuma palavra-chave encontrada</h3>
              <p>Tente ajustar os filtros ou aguarde a coleta de dados</p>
            </EmptyState>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableHeaderRow>
                    <TableHeaderCell>
                      <Checkbox
                        checked={
                          selectedKeywords.length === filteredKeywords.length &&
                          filteredKeywords.length > 0
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </TableHeaderCell>
                    <TableHeaderCell $sortable>Palavra Chave ↕</TableHeaderCell>
                    <TableHeaderCell $sortable>Posição Atual ↕</TableHeaderCell>
                    <TableHeaderCell $sortable>
                      Última Posição ↕
                    </TableHeaderCell>
                    <TableHeaderCell>Variação</TableHeaderCell>
                    <TableHeaderCell>Busca no Maps</TableHeaderCell>
                  </TableHeaderRow>
                </TableHeader>
                <TableBody>
                  {paginatedKeywords.map((keyword) => {
                    const diff = rankingExibido(keyword) - keyword.lastRanking;
                    const trendInfo = getTrendInfo(diff);
                    const kwId = String(keyword.id);

                    return (
                      <TableRow
                        key={keyword.id}
                        $selected={selectedKeywords.includes(kwId)}
                      >
                        <CheckboxCell>
                          <Checkbox
                            checked={selectedKeywords.includes(kwId)}
                            onChange={() => handleSelectKeyword(kwId)}
                          />
                        </CheckboxCell>
                        <KeywordCell
                        // onClick={() => handleOpenKeywordDetails(keyword)}
                        >
                          {keyword.title}
                          {keyword.isExtra && (
                            <span
                              style={{
                                marginLeft: 6,
                                fontSize: 10,
                                color: "#718096",
                              }}
                            >
                              (extra)
                            </span>
                          )}
                        </KeywordCell>
                        <MonthCell>
                          {rankingExibido(keyword) > 0
                            ? rankingExibido(keyword)
                            : "-"}
                        </MonthCell>
                        <MonthCell>
                          {keyword.lastRanking > 0 ? keyword.lastRanking : "-"}
                        </MonthCell>
                        <TotalCell>
                          {diff !== 0 && (
                            <TrendBadge $trend={trendInfo.trend}>
                              {trendInfo.display}
                            </TrendBadge>
                          )}
                          {diff === 0 && (
                            <span style={{ color: "#718096" }}>-</span>
                          )}
                        </TotalCell>
                        <MonthCell>
                          <RankingLink href={linkDaConsulta(keyword)} compact />
                        </MonthCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <Pagination>
                <PaginationInfo>
                  Mostrando de {(currentPage - 1) * itemsPerPage + 1} até{" "}
                  {Math.min(
                    currentPage * itemsPerPage,
                    filteredKeywords.length,
                  )}{" "}
                  de {filteredKeywords.length} registros
                </PaginationInfo>
                <PaginationButtons>
                  <PaginationButton
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    ‹
                  </PaginationButton>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <PaginationButton
                        key={page}
                        $active={currentPage === page}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </PaginationButton>
                    );
                  })}
                  <PaginationButton
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                  >
                    ›
                  </PaginationButton>
                </PaginationButtons>
              </Pagination>
            </>
          )}
        </TableContainer>

        {!isLoading && keywords.length > 0 && (
          <KeywordsChart keywords={keywords} />
        )}

        {/* <KeywordDetailsModal
          open={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          keyword={selectedKeywordDetail}
        /> */}
      </ContentContainer>
    </Container>
  );
};
